<template>
  <div class="container">
    <div class="columns">
      <div class="column content">
        <h3 class="has-text-centered-mobile">
          Submitted Invoices
        </h3>
        <div :class="{ 'hide-edges': hideEdges }">
          <div
            v-if="loading"
            class="loading"
          >
            <div class="spinner">
              <i class="fas fa-spinner fa-4x fa-spin" />
            </div>
          </div>

          <v-server-table
            ref="invoices"
            :columns="tableColumns"
            :options="tableOptions"
            @loading="loading = true"
            @loaded="loaded"
          >
            <template slot="beforeTable">
              <div class="columns">
                <div class="column is-narrow">
                  <label class="label">Search by vendor name or EIN</label>
                  <div class="field has-addons">
                    <p class="control is-marginless">
                      <textbox
                        id="filter-by"
                        v-model="query"
                        type="text"
                        placeholder="Search"
                        @input="updateQuery"
                      />
                    </p>
                  </div>
                </div>

                <div
                  v-if="isAdmin"
                  class="column is-narrow"
                >
                  <label class="label">Filter by department</label>
                  <div class="field has-addons max-width-300">
                    <p class="control is-marginless">
                      <dropdown
                        v-model="departmentId"
                        aria-label="Filter by department"
                        :options="this.departments"
                        label="Select department"
                        placeholder="Select Department"
                      />
                    </p>
                  </div>
                </div>

                <div class="column is-narrow">
                  <label class="label">Submitted from</label>
                  <div class="field has-addons">
                    <p class="control is-marginless">
                      <textbox
                        id="filter-by"
                        v-model="submittedFrom"
                        type="date"
                        placeholder="Start Date"
                        @input="updateSubmittedFrom"
                      />
                    </p>
                  </div>
                </div>

                <div class="column is-narrow">
                  <label class="label">Submitted to</label>
                  <div class="field has-addons">
                    <p class="control is-marginless">
                      <textbox
                        id="filter-by"
                        v-model="submittedTo"
                        type="date"
                        placeholder="End Date"
                        @input="updateSubmittedTo"
                      />
                    </p>
                  </div>
                </div>

              </div>
              <div class="columns">

                <div class="column is-narrow">
                  <div class="field has-addons">
                    <p class="control is-marginless">
                      <button
                        class="button is-primary"
                        @click="clearFilters"
                      >
                        Clear filters
                      </button>
                    </p>
                  </div>
                </div>

                <div class="column is-narrow">
                  <div class="field has-addons">
                    <p class="control is-marginless">
                      <button
                        class="button is-primary"
                        @click="openExportPopup"
                      >
                        CSV
                      </button>
                    </p>
                  </div>
                </div>
              </div>

              <p
                v-show="filterIsEIN"
                class="has-text-danger"
              >
                <small>Search By EIN uses the last 4 digits only</small>
              </p>
            </template>

            <template
              slot="submittedOn"
              slot-scope="props"
            >
              {{ props.row.submittedOn ? $dayjs.utc(props.row.submittedOn).local().format('M/DD/YYYY h:mm A') : '' }}
            </template>

            <template
              slot="invoiceAmount"
              slot-scope="props"
            >
              {{ getInvoiceAmount(props.row.invoiceAmount) }}
            </template>

            <template
              slot="invoiceType"
              slot-scope="props"
            >
              {{ getInvoiceType(props.row.invoiceType) }}
            </template>

            <template
              slot="confirmationNumber"
              slot-scope="props"
            >
              <router-link
                :to="`/invoice/${props.row.confirmationNumber}`"
                class=""
              >
                {{ props.row.confirmationNumber }}
              </router-link>
            </template>
          </v-server-table>
        </div>
      </div>
    </div>
    <modal
      ref="export-data"
      class="is-small"
    >
      <div v-if="!csvLoading">
        You are about to download {{ this.totalCount }} rows of data.
      </div>
      <div v-if="csvLoading">
        You are downloading {{ this.totalCount }} rows of data.
      </div>

      <div
        v-if="csvLoading"
        class="modal-spinner"
      >
        <i class="fas fa-spinner fa-4x fa-spin" />
      </div>

      <button
        class="button is-primary"
        :disabled="csvLoading"
        @click="downloadCSV"
      >
        Export CSV
      </button>
    </modal>
  </div>
</template>

<script>

import Vue from 'vue';

import Modal from '@/components/common/Modal.vue';

import {
  Dropdown,
  Textbox,
} from '@phila/phila-ui';

import { ServerTable } from 'vue-tables-2';
import { debounce } from 'lodash';

import { options }  from '@/config/tableOptions';
import { update } from 'lodash';
import { get } from 'lodash';

const tableColumns = [
  "confirmationNumber",
  "submittedOn",
  "vendorName",
  "ein",
  "department",
  "unit",
  "invoiceType",
  "invoiceNumber",
  "invoiceAmount",
];

const csvColumns = tableColumns.concat([
  "purchaseOrderNumber",
  "hasContractNum",
  "contractNum",
]);

Vue.use(
  ServerTable,
  {},
  false,
  'bulma'
);

export default {
  name: 'Invoices',
  components: {
    Modal,
    Dropdown,
    Textbox,
  },
  data() {
    return {
      currentData: [],
      filterIsEIN: false,
      totalCount: false,
      loading: false,
      csvLoading: false,
      query: '',
      departmentId: '',
      submittedFrom: '',
      submittedTo: '',
      tableColumns,
      tableOptions: options({
        requestFunction: this.customRequestFunction,
        responseAdapter: this.customResponseAdapter,
        headings: {
          confirmationNumber: "Confirmation Number",
          submittedOn: "Submitted On",
          vendorName: "Vendor Name",
          ein: "EIN",
          department: "Department",
          unit: "Unit",
          invoiceType: "Invoice Type",
          invoiceNumber: "Invoice Number",
          invoiceAmount: "Invoice Amount",
        },
        filterable: [],
        resizableColumns: true,
      }),
    };
  },
  computed: {
    departments() {
      const departmentsArray = this.$store.state.departments.departments;
      const departments = {};
      departmentsArray.forEach((department) => {
        departments[department.id] = department.name;
      });
      return departments;
    },
    hideEdges() {
      return (Math.ceil(this.totalCount / this.tableOptions.perPage) < this.tableOptions.pagination.chunk);
    },
  },
  watch: {
    departmentId(nextDepartmentId) {
      console.log('departmentId changed:', nextDepartmentId);
      this.$refs.invoices.refresh();
    },
  },
  async fetch({ store }) {
    await store.dispatch('departments/getAllDepartments');
  },
  methods: {
    loaded (data) {
      this.loading = false;
      this.totalCount = data.totalCount;
    },
    getInvoiceAmount(amount) {
      if (amount === null || amount === undefined || isNaN(amount) || amount === '') {
        return '$0.00';
      }
      return '$'+parseFloat(amount).toFixed(2);
    },
    getInvoiceType(type) {
      console.log('getInvoiceType type:', type);
      const invoiceTypes = {
        'purchase-order-invoice': 'Purchase order',
        'professional-services-invoice': 'Professional services',
        'public-works-invoice': 'Public Works',
        'other': 'Other',
      };
      return invoiceTypes[type] || 'Unknown';
    },
    clearFilters() {
      this.query = '';
      this.departmentId = '';
      this.submittedFrom = '';
      this.submittedTo = '';
      this.$refs.invoices.refresh();
    },
    openExportPopup() {
      this.$refs['export-data'].show();
    },
    closeExportPopup() {
      this.$refs['export-data'].hide();
    },
    async downloadCSV() {
      console.log('downloadCSV is running');
      this.csvLoading = true;
      const fileName = `invoices`;
      const params = {
        perPage: 100000,
        limit: 100000,
      };
      const query = { ...params, ...{ query: this.query, departmentId: this.departmentId, submittedFrom: this.submittedFrom, submittedTo: this.submittedTo }};
      const { data } = await this.$store.dispatch('invoices/getAllInvoices', query);
      const invoices = data.invoices.map((invoice) => {
        return {
          confirmationNumber: invoice.confirmationNumber,
          submittedOn: invoice.submittedOn ? this.$dayjs.utc(invoice.submittedOn).local().format('M/DD/YYYY h:mm A') : '',
          vendorName: invoice.vendorName,
          ein: invoice.ein,
          department: invoice.department,
          unit: invoice.unit,
          invoiceType: this.getInvoiceType(invoice.invoiceType),
          invoiceNumber: invoice.invoiceNumber,
          invoiceAmount: this.getInvoiceAmount(invoice.invoiceAmount),
          purchaseOrderNumber: invoice.purchaseOrderNumber,
          hasContractNum: invoice.hasContractNum,
          contractNum: invoice.contractNum,
        };
      });

      invoices.unshift(csvColumns);
      this.$helpers.downloadCSV(invoices, fileName);
      this.csvLoading = false;
      this.closeExportPopup();
    },
    async customRequestFunction(params) {
      console.log('customRequestFunction params:', params);
      const query = { ...params, ...{ query: this.query, departmentId: this.departmentId, submittedFrom: this.submittedFrom, submittedTo: this.submittedTo }};
      try {
        // console.log('customRequestFunction params:', params);
        const { data } = await this.$store.dispatch('invoices/getInvoices', query);

        // console.log('customRequestFunction data:', data);
        return data;
      } catch (err) {
        // console.error('customRequestFunction error:', err);
        return [];
      }
    },

    customResponseAdapter(data) {
      console.log('customResponseAdapter data:', data, 'typeof data:', typeof data);
      const resp = { data: [], count: 0 };
      if (typeof data === "object") {
        // && typeof data.data === "object") {
        resp.data = data.invoices;
        resp.count = data.totalCount;
      }
      // console.log('customResponseAdapter resp:', resp);
      return resp;
    },

    updateQuery: debounce(function(value) {
      console.log('updateQuery value:', value);
      this.$refs.invoices.refresh();
    }, 750),

    updateDepartmentId (value) {
      console.log('updateDepartmentId value:', value);
      this.$refs.invoices.refresh();
    },

    updateSubmittedFrom (value) {
      const test = String(value).match(/\d{1,2}(\/|-)\d{1,2}(\/|-)\d{2,4}/);
      if (test) {
        this.$refs.invoices.refresh();
      }
      console.log('updateSubmittedFrom value:', value, 'test:', test);
    },
    updateSubmittedTo (value) {
      const test = String(value).match(/\d{1,2}(\/|-)\d{1,2}(\/|-)\d{2,4}/);
      if (test) {
        this.$refs.invoices.refresh();
      }
    },

  },

};
</script>

<style lang="scss">

.loading {
  position: absolute;
  top: 30;
  width: 100%;
  height: 96%;
  background-color: $grey-medium;
  opacity: 0.8;
  z-index: 10;
  border-radius: 10px;
}

.modal-spinner {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.spinner {
	position: relative;
  opacity: 1;
	left: 45%;
	top: 100px;
  z-index: 11;
}

@media (min-width: 768px) {
  .max-width-300 {
    max-width: 300px;
  }
}

.clear-filters-button {
  margin-top: 3rem;
}

@media (max-width: 767px) {
  .submit-button {
    margin-top: 0rem;
  }
}

.input-dropdown {
  select {
    padding-right: 3rem !important;
  }
}

.is-current {
  .is-current {
    background-color: $ben-franklin-blue-light;
    // background-color: #25cef7;
  }
}

</style>
